import React, { memo } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Modal,
    Backdrop,
    Fade,
    Paper,
} from '@mui/material';

import useModalEmprestimoUpdateDadosCadastrais from './useModalEmprestimoUpdateDadosCadastrais';

const ModalEmprestimoUpdateDadosCadastrais = ({
    showLoanModal,
    setShowLoanModal,
    loanData,
    loadingLoanData,
    handleLoanFormSubmit,
}) => {
    const {
        formData,
        bankDataType,
        setBankDataType,
        handleInputChange,
        handleNestedInputChange,
        handleIdNumberChange,
        handleSubmit,
        setFormData,
        brazilianStates,
        genderOptions,
        civilStatusOptions,
        accountTypeOptions,
    } = useModalEmprestimoUpdateDadosCadastrais({
        showLoanModal,
        setShowLoanModal,
        loanData,
        loadingLoanData,
        handleLoanFormSubmit,
    });

    return (
        <Modal
            open={showLoanModal}
            onClose={() => setShowLoanModal(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={showLoanModal}>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
                        maxHeight: '90vh',
                        overflow: 'auto',
                        p: 4,
                        boxShadow: 24,
                    }}
                >
                    {loadingLoanData ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <Typography variant="h5" component="h2" gutterBottom>
                                Dados Complementares do Empréstimo
                            </Typography>

                            {/* Personal Information */}
                            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                                Dados Pessoais
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nome Completo"
                                        value={formData.takerRegister.customerName}
                                        InputProps={{ readOnly: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nome da Mãe"
                                        value={formData.takerRegister.motherName}
                                        InputProps={{ readOnly: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nome do Pai"
                                        value={formData.takerRegister.fatherName}
                                        onChange={(e) =>
                                            handleInputChange('takerRegister', 'fatherName', e.target.value)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Gênero</InputLabel>
                                        <Select value={formData.takerRegister.gender} inputProps={{ readOnly: true }}>
                                            {genderOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="RG/Documento"
                                        value={formData.takerRegister.idNumber}
                                        onChange={(e) => handleIdNumberChange(e.target.value)}
                                        placeholder="Ex: 123456789"
                                        inputProps={{ maxLength: 15 }}
                                        helperText="Máximo 15 caracteres. Apenas letras, números, pontos e hífens."
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Órgão Emissor"
                                        value={formData.takerRegister.emissionEntity}
                                        onChange={(e) =>
                                            handleInputChange('takerRegister', 'emissionEntity', e.target.value)
                                        }
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Data de Emissão"
                                        type="date"
                                        value={formData.takerRegister.emissionDate}
                                        onChange={(e) =>
                                            handleInputChange('takerRegister', 'emissionDate', e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Estado Emissor</InputLabel>
                                        <Select
                                            value={formData.takerRegister.emissionState}
                                            onChange={(e) =>
                                                handleInputChange('takerRegister', 'emissionState', e.target.value)
                                            }
                                        >
                                            {brazilianStates.map((state) => (
                                                <MenuItem key={state} value={state}>
                                                    {state}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Estado Civil</InputLabel>
                                        <Select
                                            value={formData.takerRegister.civilStatus}
                                            onChange={(e) =>
                                                handleInputChange('takerRegister', 'civilStatus', e.target.value)
                                            }
                                        >
                                            {civilStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Naturalidade"
                                        value={formData.takerRegister.birthplace}
                                        onChange={(e) =>
                                            handleInputChange('takerRegister', 'birthplace', e.target.value)
                                        }
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Nacionalidade</InputLabel>
                                        <Select
                                            value={formData.takerRegister.nationality}
                                            onChange={(e) =>
                                                handleInputChange('takerRegister', 'nationality', e.target.value)
                                            }
                                        >
                                            <MenuItem value="1">Brasileira</MenuItem>
                                            <MenuItem value="2">Estrangeira</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Work Information */}
                            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                                Dados Profissionais
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Local de Trabalho"
                                        value={formData.takerData.workPlaceName}
                                        onChange={(e) =>
                                            handleInputChange('takerData', 'workPlaceName', e.target.value)
                                        }
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cargo/Função"
                                        value={formData.takerData.workRoleName}
                                        onChange={(e) => handleInputChange('takerData', 'workRoleName', e.target.value)}
                                        required
                                    />
                                </Grid>
                            </Grid>

                            {/* Address Information */}
                            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                                Endereço
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="CEP"
                                        value={formData.takerAddress.zipCode}
                                        onChange={(e) => {
                                            // Only allow digits and limit to 8 characters
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                                            handleInputChange('takerAddress', 'zipCode', value);
                                        }}
                                        placeholder="00000000"
                                        inputProps={{ maxLength: 8 }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Endereço"
                                        value={formData.takerAddress.address}
                                        onChange={(e) => handleInputChange('takerAddress', 'address', e.target.value)}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Número"
                                        value={formData.takerAddress.addressNumber}
                                        onChange={(e) =>
                                            handleInputChange('takerAddress', 'addressNumber', e.target.value)
                                        }
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Complemento"
                                        value={formData.takerAddress.complement}
                                        onChange={(e) =>
                                            handleInputChange('takerAddress', 'complement', e.target.value)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Bairro"
                                        value={formData.takerAddress.neighborhood}
                                        onChange={(e) =>
                                            handleInputChange('takerAddress', 'neighborhood', e.target.value)
                                        }
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Cidade"
                                        value={formData.takerAddress.city}
                                        onChange={(e) => handleInputChange('takerAddress', 'city', e.target.value)}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Estado</InputLabel>
                                        <Select
                                            value={formData.takerAddress.state}
                                            onChange={(e) => handleInputChange('takerAddress', 'state', e.target.value)}
                                        >
                                            {brazilianStates.map((state) => (
                                                <MenuItem key={state} value={state}>
                                                    {state}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Bank Information */}
                            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                                Dados Bancários
                            </Typography>

                            {/* Bank Data Type Selection */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Tipo de Dados Bancários</InputLabel>
                                        <Select value={bankDataType} onChange={(e) => setBankDataType(e.target.value)}>
                                            <MenuItem value="account">Dados da Conta Bancária</MenuItem>
                                            <MenuItem value="pix">Dados PIX</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Conditional Bank Data Fields */}
                            {bankDataType === 'account' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Código do Banco"
                                            value={formData.takerBankData.accountData.bankCode}
                                            onChange={(e) => {
                                                // Only allow digits and limit to 3 characters
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                handleNestedInputChange(
                                                    'takerBankData',
                                                    'accountData',
                                                    'bankCode',
                                                    value,
                                                );
                                            }}
                                            placeholder="000"
                                            inputProps={{ maxLength: 3 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Agência"
                                            value={formData.takerBankData.accountData.agencyNumber}
                                            onChange={(e) => {
                                                // Only allow digits and limit to 4 characters
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                handleNestedInputChange(
                                                    'takerBankData',
                                                    'accountData',
                                                    'agencyNumber',
                                                    value,
                                                );
                                            }}
                                            placeholder="0000"
                                            inputProps={{ maxLength: 4 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Conta"
                                            value={formData.takerBankData.accountData.accountNumber}
                                            onChange={(e) => {
                                                // Only allow digits and limit to 13 characters
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                                                handleNestedInputChange(
                                                    'takerBankData',
                                                    'accountData',
                                                    'accountNumber',
                                                    value,
                                                );
                                            }}
                                            placeholder="00000000000"
                                            inputProps={{ maxLength: 13 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Dígito"
                                            value={formData.takerBankData.accountData.accountDigit}
                                            onChange={(e) => {
                                                // Only allow digits and limit to 2 characters
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                handleNestedInputChange(
                                                    'takerBankData',
                                                    'accountData',
                                                    'accountDigit',
                                                    value,
                                                );
                                            }}
                                            placeholder="0"
                                            inputProps={{ maxLength: 2 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Tipo de Conta</InputLabel>
                                            <Select
                                                value={formData.takerBankData.accountData.accountType}
                                                onChange={(e) =>
                                                    handleNestedInputChange(
                                                        'takerBankData',
                                                        'accountData',
                                                        'accountType',
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                {accountTypeOptions.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )}

                            {/* PIX Information */}
                            {bankDataType === 'pix' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Tipo da Chave PIX</InputLabel>
                                            <Select
                                                value={formData.takerBankData.pixData?.keyType || ''}
                                                onChange={(e) =>
                                                    handleNestedInputChange(
                                                        'takerBankData',
                                                        'pixData',
                                                        'keyType',
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                <MenuItem value="">Selecione...</MenuItem>
                                                <MenuItem value="1">Email</MenuItem>
                                                <MenuItem value="2">Telefone Celular</MenuItem>
                                                <MenuItem value="3">CPF</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Chave PIX"
                                            value={formData.takerBankData.pixData?.pixKey || ''}
                                            onChange={(e) =>
                                                handleNestedInputChange(
                                                    'takerBankData',
                                                    'pixData',
                                                    'pixKey',
                                                    e.target.value,
                                                )
                                            }
                                            placeholder={
                                                formData.takerBankData.pixData?.keyType === '1'
                                                    ? 'email@exemplo.com'
                                                    : formData.takerBankData.pixData?.keyType === '2'
                                                      ? '+551199999-9999'
                                                      : formData.takerBankData.pixData?.keyType === '3'
                                                        ? '000.000.000-00'
                                                        : 'Selecione o tipo da chave primeiro'
                                            }
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {/* Employee CPF */}
                            <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
                                CPF do Funcionário
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="CPF do Funcionário"
                                        value={formData.nuEmployeeCPF}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, nuEmployeeCPF: e.target.value }))
                                        }
                                        placeholder="000.000.000-00"
                                        required
                                    />
                                </Grid>
                            </Grid>

                            {/* Actions */}
                            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button variant="outlined" onClick={() => setShowLoanModal(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit" variant="contained" color="primary">
                                    Salvar Dados
                                </Button>
                            </Box>
                        </form>
                    )}
                </Paper>
            </Fade>
        </Modal>
    );
};

export default memo(ModalEmprestimoUpdateDadosCadastrais);
